import { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, ShieldCheck, Truck, RefreshCw, MinusCircle, PlusCircle } from "lucide-react";
import { api, imageUrl, formatPrice } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";
import ShareButton from "@/components/storefront/ShareButton";
import { sanitizeWhatsAppNumber, buildWhatsAppUrl } from "@/components/storefront/WhatsAppFAB";
import RazorpayAffordability from "@/components/storefront/RazorpayAffordability";
import SEO from "@/components/SEO";
import {
    absoluteImage,
    breadcrumbJsonLd,
    productJsonLd,
    productSeoDescription,
    productSeoTitle,
    STORE_NAME,
} from "@/lib/seo";

// Trim hidden leading/trailing whitespace from variant attribute values.
// Production data sometimes has stray spaces (e.g. " 10 CM" vs "10 CM") that
// otherwise break variant matching and produce duplicate option buttons.
const normAttr = (v) => (typeof v === "string" ? v.trim() : v);

function getAttributeOptions(product) {
    const schema = product.attributes_schema || [];
    const map = {};
    schema.forEach((k) => (map[k] = []));
    (product.variants || []).forEach((v) => {
        if (!v.active) return;
        schema.forEach((k) => {
            const val = normAttr(v.attributes?.[k]);
            if (val && !map[k].includes(val)) map[k].push(val);
        });
    });
    return map;
}

function findVariant(product, selection) {
    return (product.variants || []).find((v) => {
        if (!v.active) return false;
        return Object.entries(selection).every(
            ([k, val]) => normAttr(v.attributes?.[k]) === normAttr(val)
        );
    });
}

// ---------------------------------------------------------------------------
// Asymmetric availability — SIZE-like attrs are PRIMARY (never filtered);
// COLOR is SECONDARY (filtered by primary axes). When user changes a primary
// axis (e.g. clicks 8CM), any secondary whose current value is not valid in
// the new combination auto-snaps to the first available option for that axis.
// "color" is the universal secondary heuristic; everything else is primary.
// This keeps the logic schema-driven for 1-attr / 2-attr / N-attr products.
// ---------------------------------------------------------------------------
const isInStock = (v) => v && v.active && Number(v.stock || 0) > 0;
const isColorAttr = (k) => typeof k === "string" && k.trim().toLowerCase() === "color";
const isPrimaryAttr = (k) => !isColorAttr(k);

function isOptionAvailable(product, selection, key, opt) {
    const schema = product.attributes_schema || [];
    const target = normAttr(opt);
    return (product.variants || []).some((v) => {
        if (!isInStock(v)) return false;
        if (normAttr(v.attributes?.[key]) !== target) return false;
        if (isPrimaryAttr(key)) return true; // primary axis ignores other selections
        // Secondary (color) axis: filter by every PRIMARY axis currently selected.
        return schema.every((k) => {
            if (k === key || !isPrimaryAttr(k)) return true;
            return normAttr(v.attributes?.[k]) === normAttr(selection[k]);
        });
    });
}

// First in-stock option for axis `key` in the given primary-axis context.
// Used to auto-snap the COLOR when SIZE changes and the current color is
// no longer valid for the new size.
function firstAvailableOption(product, selection, key) {
    const opts = getAttributeOptions(product)[key] || [];
    return opts.find((o) => isOptionAvailable(product, selection, key, o));
}

// Build the next selection after a click. For a primary-axis click (e.g.
// size), if any SECONDARY (color) becomes invalid, snap it to the first
// available secondary in the new context.
function applyOptionClick(product, prev, key, opt) {
    const next = { ...prev, [key]: opt };
    if (!isPrimaryAttr(key)) return next;
    const schema = product.attributes_schema || [];
    schema.forEach((k) => {
        if (k === key || isPrimaryAttr(k)) return;
        const cur = next[k];
        if (!cur || !isOptionAvailable(product, next, k, cur)) {
            const fallback = firstAvailableOption(product, next, k);
            if (fallback) next[k] = fallback;
        }
    });
    return next;
}

// First active + in-stock variant (preserves admin's variant order). Used
// for initial selection so the page never lands on an out-of-stock combo.
function firstInStockVariant(product) {
    return (product.variants || []).find(isInStock);
}

// Determine which attribute drives the product's visual image. Prefer a
// "color" key (case-insensitive, whitespace-tolerant); otherwise fall back
// to the first attribute in the schema. Returns null if there is no schema.
function imageAttrKey(product) {
    const schema = product.attributes_schema || [];
    const colorKey = schema.find(
        (k) => typeof k === "string" && k.trim().toLowerCase() === "color"
    );
    return colorKey || schema[0] || null;
}

// Resolve the image_url to display for the current selection. Image follows
// the image-driving attribute (color) so that switching only "size" within
// the same color keeps the same picture. Falls back to the exact-match
// variant image, then null.
function pickVariantImage(product, selection) {
    const key = imageAttrKey(product);
    if (key) {
        const target = normAttr(selection[key]);
        if (target) {
            const match = (product.variants || []).find(
                (v) =>
                    v.active &&
                    normAttr(v.attributes?.[key]) === target &&
                    v.image_url
            );
            if (match?.image_url) return match.image_url;
        }
    }
    return findVariant(product, selection)?.image_url || "";
}

export default function ProductDetail() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const { settings } = useSettings();
    const { addItem } = useCart();
    const [product, setProduct] = useState(null);
    const [activeImg, setActiveImg] = useState(0);
    const [selection, setSelection] = useState({});
    const [qty, setQty] = useState(1);
    const [loading, setLoading] = useState(true);
    // Distinguish a real 404 (slug does not exist) from a transient API error
    // (timeout, 5xx, rate limit, Cloudflare bot challenge). Only a true 404
    // should ever trigger the noindex "Product not found" page; transient
    // errors must not cause Google to drop a real product from the index.
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        setLoading(true);
        setNotFound(false);
        api.get(`/store/products/${slug}`)
            .then((r) => {
                setProduct(r.data);
                if (r.data.product_type === "variant" && r.data.attributes_schema?.length) {
                    const initial = {};
                    // Prefer the first active + in-stock variant so the page
                    // never lands on a sold-out combo. Falls back to the first
                    // value of each attribute if nothing is in stock.
                    const seed = firstInStockVariant(r.data);
                    if (seed) {
                        r.data.attributes_schema.forEach((k) => {
                            const val = normAttr(seed.attributes?.[k]);
                            if (val) initial[k] = val;
                        });
                    } else {
                        const opts = getAttributeOptions(r.data);
                        r.data.attributes_schema.forEach((k) => {
                            if (opts[k]?.length) initial[k] = opts[k][0];
                        });
                    }
                    setSelection(initial);
                }
            })
            .catch((err) => {
                // Only treat HTTP 404 as a real "not found" → noindex. Any
                // other failure (network, 5xx, rate limit, bot challenge)
                // keeps the page indexable-by-default so we don't accidentally
                // drop a valid product from Google's index when our API
                // hiccups during Googlebot's render budget.
                if (err?.response?.status === 404) {
                    setNotFound(true);
                }
                setProduct(null);
            })
            .finally(() => setLoading(false));
    }, [slug]);

    const images = useMemo(() => {
        if (!product) return [];
        const list = [...(product.images || [])];
        if (product.primary_image && !list.includes(product.primary_image)) {
            list.unshift(product.primary_image);
        }
        return list.length ? list : [""];
    }, [product]);

    const variant = useMemo(() => {
        if (!product || product.product_type !== "variant") return null;
        return findVariant(product, selection);
    }, [product, selection]);

    // Image to display follows the COLOR attribute (not size). Same color +
    // different size keeps the same picture.
    const variantImage = useMemo(() => {
        if (!product || product.product_type !== "variant") return "";
        return pickVariantImage(product, selection);
    }, [product, selection]);

    // When the resolved variant image is in the main gallery, sync activeImg.
    useEffect(() => {
        if (!product || !variantImage) return;
        const list = [...(product.images || [])];
        if (product.primary_image && !list.includes(product.primary_image)) list.unshift(product.primary_image);
        const idx = list.indexOf(variantImage);
        if (idx >= 0) {
            setActiveImg(idx);
        }
        // If variant image isn't in the main gallery, the gallery shows a "variant view" overlay (handled below).
    }, [variantImage, product]);

    // The image to show in the main viewer (color-resolved variant image takes priority when set)
    const mainImageSrc = useMemo(() => {
        if (variantImage) return imageUrl(variantImage, "large");
        return imageUrl(images[activeImg] || "", "large");
    }, [variantImage, images, activeImg]);

    if (loading) {
        // Render a minimal SEO with index,follow to neutralize any stale
        // robots meta left over from a previous route (e.g. /cart, /shop?sale=1)
        // that the SEO component does not clear on unmount. This guarantees
        // Googlebot never snapshots a "noindex" tag while the API resolves.
        return (
            <div className="container mx-auto px-4 py-12 text-white/55" data-testid="product-loading">
                <SEO
                    title={STORE_NAME}
                    description={`Loading product details on ${STORE_NAME}.`}
                    path={`/product/${slug}`}
                />
                Loading…
            </div>
        );
    }
    if (!product) {
        // Distinguish a real 404 (noindex) from a transient API error
        // (default to indexable). Transient errors must not get a valid
        // product de-indexed by Google.
        if (notFound) {
            return (
                <div className="container mx-auto px-4 py-16 text-center" data-testid="product-not-found">
                    <SEO
                        title={`Product not found | ${STORE_NAME}`}
                        description="The product you're looking for is no longer available."
                        path={`/product/${slug}`}
                        noindex
                    />
                    <h2 className="font-display text-2xl font-black uppercase">Product not found</h2>
                    <p className="text-white/55 mt-2 max-w-md mx-auto text-sm">
                        The item you're looking for may have been removed or is temporarily unavailable.
                    </p>
                    <Link to="/shop" className="text-[#00BCE5] mt-3 inline-block">
                        Back to shop
                    </Link>
                </div>
            );
        }
        // Transient API failure (network / 5xx / Cloudflare challenge / Googlebot
        // fetch issue). Do NOT replace the prerendered product content with an
        // error UI — that would mislabel the page as soft-404 in Google Search
        // Console. Instead:
        //  • Re-render the prerendered SEO body (captured in index.js before
        //    React mounted) so Googlebot/social previews still see real product
        //    content after JS executes.
        //  • Keep robots=index,follow and canonical = product URL.
        //  • Show a small non-blocking retry banner for real users.
        const prerenderedHtml = typeof window !== "undefined" ? window.__OFT_PRERENDER_HTML__ : "";
        return (
            <div data-testid="product-transient-error">
                <SEO
                    title={STORE_NAME}
                    description={`Loading product details on ${STORE_NAME}.`}
                    path={`/product/${slug}`}
                />
                {prerenderedHtml ? (
                    <div dangerouslySetInnerHTML={{ __html: prerenderedHtml }} />
                ) : (
                    // Bare-minimum visible content if the prerender capture
                    // wasn't available (e.g. SPA fallback path). Still keeps
                    // the page indexable.
                    <div className="container mx-auto px-4 py-16 text-center">
                        <h1 className="font-display text-2xl font-black uppercase">
                            {slug
                                .replace(/-/g, " ")
                                .toUpperCase()}
                        </h1>
                        <p className="text-white/55 mt-2 max-w-md mx-auto text-sm">
                            Live product details could not refresh. Please reload or try again.
                        </p>
                        <Link to="/shop" className="text-[#00BCE5] mt-3 inline-block">
                            Browse shop
                        </Link>
                    </div>
                )}
                <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-[#1f1f23] border border-[#FF6E66]/30 text-[#FF9F8F] text-xs px-4 py-2 rounded-sm shadow-lg z-50" role="status">
                    Live product details could not refresh. Please reload or try again.
                </div>
            </div>
        );
    }
    // Thin-content guard: if a product is missing BOTH a primary image AND any
    // meaningful description, mark it noindex so Google doesn't surface a thin
    // page (a soft-404 risk). The page still renders normally for users; only
    // the meta tag is added.
    const hasImage = !!(product.primary_image || (product.images && product.images[0]));
    const hasCopy = !!((product.short_description && product.short_description.trim()) ||
                       (product.description && product.description.trim()));
    const isThinContent = !hasImage && !hasCopy;

    const cur = settings?.currency_symbol || "Rs.";
    const isVariant = product.product_type === "variant";
    const price = isVariant
        ? Number(variant?.sale_price || variant?.price || 0)
        : Number(product.sale_price || product.price);
    const listPrice = isVariant ? Number(variant?.price || 0) : Number(product.price);
    const onSale = listPrice > price;
    const stock = isVariant ? Number(variant?.stock || 0) : Number(product.stock || 0);
    const available = stock > 0 && (isVariant ? !!variant : true);

    const handleAdd = () => {
        if (!available) {
            toast.error("This selection is out of stock");
            return;
        }
        const img = imageUrl(variantImage || product.primary_image || images[0]);
        addItem({
            product_id: product.id,
            variant_id: variant?.id || "",
            name: product.name,
            variant_label: variant?.label || "",
            sku: variant?.sku || product.sku,
            price,
            quantity: Math.max(1, qty),
            image: img,
            slug: product.slug,
        });
        toast.success(`Added to cart`, { description: product.name });
    };

    const buyNow = () => {
        handleAdd();
        navigate("/cart");
    };

    return (
        <div className="container mx-auto px-4 py-4 md:py-10" data-testid="product-detail-page">
            <SEO
                title={productSeoTitle(product)}
                description={productSeoDescription(product)}
                path={`/product/${product.slug}`}
                image={absoluteImage(product.primary_image || (product.images && product.images[0]), "large")}
                ogType="product"
                noindex={isThinContent}
                jsonLd={[
                    productJsonLd(product, price),
                    breadcrumbJsonLd([
                        { name: "Home", path: "/" },
                        { name: "Shop", path: "/shop" },
                        ...(product.category
                            ? [{ name: product.category.name, path: `/shop?category=${product.category.slug}` }]
                            : []),
                        { name: product.name, path: `/product/${product.slug}` },
                    ]),
                ]}
            />
            <nav className="text-[11px] uppercase tracking-widest text-white/45 mb-3 font-display flex items-center gap-2" aria-label="Breadcrumb">
                <Link to="/" className="hover:text-white">Home</Link> /
                <Link to="/shop" className="hover:text-white">Shop</Link> /
                {product.category && (
                    <>
                        <Link to={`/shop?category=${product.category.slug}`} className="hover:text-white">
                            {product.category.name}
                        </Link>{" "}
                        /
                    </>
                )}
                <span className="text-white/30 truncate max-w-[40vw]">{product.name}</span>
            </nav>

            <div className="grid md:grid-cols-2 gap-6 md:gap-12">
                {/* Gallery */}
                <div>
                    <div className="relative aspect-square bg-[#121214] border border-[#27272A] rounded-sm overflow-hidden">
                        {mainImageSrc ? (
                            <img
                                src={mainImageSrc}
                                alt={`${product.name} fishing tackle in India`}
                                decoding="async"
                                className="w-full h-full object-cover transition-opacity duration-200"
                                key={mainImageSrc}
                            />
                        ) : null}
                        {variantImage && (
                            <div className="absolute top-2 left-2 bg-[#00BCE5] text-black text-[10px] font-display font-bold uppercase tracking-widest px-2 py-1 rounded-sm">
                                {variant?.label || product.name}
                            </div>
                        )}
                        {images.length > 1 && (
                            <>
                                <button
                                    onClick={() => setActiveImg((i) => (i - 1 + images.length) % images.length)}
                                    className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full"
                                    aria-label="Previous image"
                                    data-testid="gallery-prev"
                                >
                                    <ChevronLeft size={18} />
                                </button>
                                <button
                                    onClick={() => setActiveImg((i) => (i + 1) % images.length)}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full"
                                    aria-label="Next image"
                                    data-testid="gallery-next"
                                >
                                    <ChevronRight size={18} />
                                </button>
                            </>
                        )}
                    </div>
                    {images.length > 1 && (
                        <div className="grid grid-cols-5 gap-2 mt-3">
                            {images.map((src, i) => (
                                <button
                                    key={i}
                                    onClick={() => setActiveImg(i)}
                                    className={`aspect-square overflow-hidden border rounded-sm bg-[#121214] ${
                                        i === activeImg ? "border-[#00BCE5]" : "border-[#27272A]"
                                    }`}
                                    data-testid={`gallery-thumb-${i}`}
                                >
                                    <img
                                        src={imageUrl(src, "thumbnail")}
                                        alt=""
                                        loading="lazy"
                                        decoding="async"
                                        className="w-full h-full object-cover"
                                    />
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                {/* Info */}
                <div>
                    {product.brand && (
                        <Link
                            to={`/shop?brand=${product.brand.slug}`}
                            className="text-xs uppercase tracking-[0.3em] text-[#00BCE5] font-display font-bold"
                        >
                            {product.brand.name}
                        </Link>
                    )}
                    <h1 className="font-display text-2xl md:text-4xl font-black uppercase tracking-tight text-white mt-2 leading-tight">
                        {product.name}
                    </h1>
                    {product.short_description && (
                        <p className="text-white/65 mt-3 leading-relaxed">{product.short_description}</p>
                    )}

                    <div className="mt-5 flex items-end gap-3">
                        <span className="font-display font-black text-3xl md:text-4xl text-white">
                            {formatPrice(price, cur)}
                        </span>
                        {onSale && (
                            <>
                                <span className="text-base price-strike">{formatPrice(listPrice, cur)}</span>
                                <span className="bg-[#FF3B30] text-white text-[10px] font-black uppercase px-2 py-1 tracking-widest">
                                    Save {Math.round(((listPrice - price) / listPrice) * 100)}%
                                </span>
                            </>
                        )}
                    </div>
                    <div className="mt-1 text-xs uppercase tracking-widest font-display">
                        {available ? (
                            <span className="text-[#34C759]">In stock{stock <= 5 ? ` — only ${stock} left` : ""}</span>
                        ) : (
                            <span className="text-[#FF3B30]">Out of stock</span>
                        )}
                    </div>

                    {/* Variant selectors */}
                    {isVariant && (
                        <div className="mt-6 space-y-4">
                            {(product.attributes_schema || []).map((key) => {
                                const opts = getAttributeOptions(product)[key] || [];
                                return (
                                    <div key={key}>
                                        <div className="text-[11px] uppercase tracking-widest font-display font-bold text-white/55 mb-2">
                                            {key.replace("_", " ")}
                                        </div>
                                        <div className="flex flex-wrap gap-2">
                                            {opts.map((opt) => {
                                                const available = isOptionAvailable(product, selection, key, opt);
                                                const disabled = !available;
                                                return (
                                                    <button
                                                        key={opt}
                                                        type="button"
                                                        className="variant-chip"
                                                        data-active={selection[key] === opt}
                                                        data-disabled={disabled}
                                                        aria-disabled={disabled}
                                                        onClick={() =>
                                                            !disabled &&
                                                            setSelection((prev) => applyOptionClick(product, prev, key, opt))
                                                        }
                                                        data-testid={`variant-${key}-${opt}`}
                                                    >
                                                        {opt}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {/* Quantity + actions */}
                    <div className="mt-6 flex items-center gap-3">
                        <div className="inline-flex items-center gap-3 border border-[#27272A] rounded-sm px-3 py-2 bg-[#121214]">
                            <button
                                onClick={() => setQty((q) => Math.max(1, q - 1))}
                                className="text-white/70 hover:text-white"
                                aria-label="Decrease"
                                data-testid="qty-decrease"
                            >
                                <MinusCircle size={20} />
                            </button>
                            <span className="font-display text-lg font-bold w-6 text-center" data-testid="qty-value">
                                {qty}
                            </span>
                            <button
                                onClick={() => setQty((q) => q + 1)}
                                className="text-white/70 hover:text-white"
                                aria-label="Increase"
                                data-testid="qty-increase"
                            >
                                <PlusCircle size={20} />
                            </button>
                        </div>
                    </div>

                    <div className="hidden md:flex gap-3 mt-5">
                        <button
                            onClick={handleAdd}
                            disabled={!available}
                            className="flex-1 bg-[#00BCE5] text-black font-display font-bold uppercase tracking-widest py-4 rounded-sm hover:bg-[#33C9EA] disabled:opacity-40 disabled:cursor-not-allowed btn-primary-glow"
                            data-testid="pdp-add-to-cart"
                        >
                            Add to cart
                        </button>
                        <button
                            onClick={buyNow}
                            disabled={!available}
                            className="flex-1 border border-white/30 text-white font-display font-bold uppercase tracking-widest py-4 rounded-sm hover:border-[#00BCE5] hover:text-[#00BCE5] disabled:opacity-40 disabled:cursor-not-allowed"
                            data-testid="pdp-buy-now"
                        >
                            Buy now
                        </button>
                    </div>

                    {/* Inquiry + Share row (additive, does not affect cart/checkout/payment) */}
                    {(() => {
                        const productUrl =
                            typeof window !== "undefined"
                                ? `${window.location.origin}/product/${product.slug}`
                                : `/product/${product.slug}`;
                        const formattedPrice = formatPrice(price, cur);
                        const waDigits = sanitizeWhatsAppNumber(settings?.whatsapp_number);
                        const inquiryMsg =
                            `Hi Opensea Fishing Tackles, I am interested in this product:\n` +
                            `Product: ${product.name}\n` +
                            `Price: ${formattedPrice}\n` +
                            `Link: ${productUrl}\n` +
                            `Please share availability and details.`;
                        const inquiryHref = waDigits ? buildWhatsAppUrl(waDigits, inquiryMsg) : null;
                        const shareTextParts = [product.name, formattedPrice];
                        if (product.short_description) shareTextParts.push(product.short_description);
                        const shareText = shareTextParts.filter(Boolean).join(" • ");
                        return (
                            <div className="mt-3 flex flex-col md:flex-row gap-3" data-testid="pdp-inquiry-share-row">
                                {inquiryHref && (
                                    <a
                                        href={inquiryHref}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center justify-center gap-2 flex-1 border border-[#25D366] text-[#25D366] font-display font-bold uppercase tracking-widest py-4 rounded-sm hover:bg-[#25D366] hover:text-white transition-colors"
                                        data-testid="pdp-ask-on-whatsapp"
                                    >
                                        <svg viewBox="0 0 32 32" width="18" height="18" fill="currentColor" aria-hidden="true">
                                            <path d="M19.11 17.27c-.27-.14-1.59-.78-1.83-.87-.25-.09-.43-.13-.61.13-.18.27-.7.87-.86 1.05-.16.18-.31.2-.58.07-.27-.14-1.13-.42-2.16-1.34-.8-.71-1.34-1.59-1.5-1.86-.16-.27-.02-.42.12-.55.12-.12.27-.31.4-.46.13-.16.18-.27.27-.45.09-.18.04-.34-.02-.48-.07-.14-.61-1.46-.84-2-.22-.53-.45-.46-.61-.47l-.52-.01c-.18 0-.48.07-.73.34-.25.27-.95.93-.95 2.27 0 1.34.97 2.63 1.11 2.81.13.18 1.92 2.93 4.65 4.11.65.28 1.16.45 1.55.58.65.21 1.24.18 1.71.11.52-.08 1.59-.65 1.81-1.27.22-.62.22-1.16.16-1.27-.06-.11-.25-.18-.52-.31zM16.05 4C9.39 4 4 9.39 4 16.04c0 2.36.69 4.55 1.88 6.4L4 28l5.69-1.84c1.78 1.05 3.85 1.66 6.36 1.66 6.66 0 12.05-5.39 12.05-12.04C28.1 9.39 22.71 4 16.05 4zm0 21.84c-2.07 0-3.99-.62-5.6-1.69l-.4-.25-3.37 1.09 1.09-3.28-.26-.42a9.83 9.83 0 0 1-1.5-5.25c0-5.43 4.42-9.85 9.84-9.85a9.84 9.84 0 0 1 9.85 9.85c0 5.43-4.42 9.8-9.65 9.8z" />
                                        </svg>
                                        Ask on WhatsApp
                                    </a>
                                )}
                                <div className={inquiryHref ? "md:w-44" : "flex-1"}>
                                    <ShareButton url={productUrl} title={product.name} text={shareText} />
                                </div>
                            </div>
                        );
                    })()}

                    {/* Razorpay Affordability Widget (display-only, Phase 1) */}
                    <div className="mt-5">
                        <RazorpayAffordability
                            keyId={settings?.razorpay_key_id}
                            amount={Math.round(price * 100)}
                            disabled={!available}
                        />
                    </div>

                    {/* Trust */}
                    <ul className="mt-6 grid grid-cols-3 gap-2 text-[11px] md:text-xs text-white/55 font-display uppercase tracking-widest">
                        <li className="flex items-center gap-2"><Truck size={14} className="text-[#00BCE5]" /> Pan-India</li>
                        <li className="flex items-center gap-2"><ShieldCheck size={14} className="text-[#00BCE5]" /> Authentic</li>
                        <li className="flex items-center gap-2"><RefreshCw size={14} className="text-[#00BCE5]" /> Easy returns</li>
                    </ul>

                    {/* Description */}
                    {product.description && (
                        <div className="mt-8 border-t border-[#27272A] pt-6">
                            <h3 className="font-display uppercase font-black text-lg mb-2">Description</h3>
                            <p className="text-white/70 leading-relaxed whitespace-pre-line">{product.description}</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Sticky mobile bar — sits above MobileBottomBar (which is ~56px tall) */}
            <div className="md:hidden fixed bottom-14 inset-x-0 z-40 bg-[#0A0A0B]/95 backdrop-blur-xl border-t border-[#27272A] p-3 grid grid-cols-2 gap-2">
                <button
                    onClick={handleAdd}
                    disabled={!available}
                    className="bg-[#00BCE5] text-black font-display font-bold uppercase tracking-widest py-3.5 rounded-sm disabled:opacity-40"
                    data-testid="pdp-add-to-cart-mobile"
                >
                    Add
                </button>
                <button
                    onClick={buyNow}
                    disabled={!available}
                    className="border border-[#00BCE5] text-[#00BCE5] font-display font-bold uppercase tracking-widest py-3.5 rounded-sm disabled:opacity-40"
                    data-testid="pdp-buy-now-mobile"
                >
                    Buy now
                </button>
            </div>
        </div>
    );
}
